import { Typography } from '@euler/app/components/typography';
import { CardNotice } from '@euler/app/flows/ai-suite/components/ChatCard';
import { SecondaryButton } from '@euler/app/flows/ai-suite/components/PrimaryButton';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { SpeechCapture } from '@euler/components/voice-input/capture/SpeechCapture';
import { SpeechCaptureHandle } from '@euler/components/voice-input/capture/speechCaptureTypes';
import { useVoiceTurn } from '@euler/components/voice-input/functions/useVoiceTurn';
import { config } from '@euler/config';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { CoachingCaseFormat, CoachingChannel } from '@euler/model/coaching';
import {
  CoachingAnchor,
  CoachingPracticeEnd,
  CoachingPracticeLine,
} from '@euler/model/coaching-practice';
import { useBehaviorSubject } from '@euler/utils/hooks/useBehaviorSubject';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KeyboardAvoidingView, Platform, View } from 'react-native';
import { BehaviorSubject } from 'rxjs';
import { channelName, levelName } from '../../copy';
import { PracticeScene } from '../functions/practiceScene';
import { TraineeVoice, traineeVoiceOf } from '../functions/traineeVoice';
import { useCoachingEdge } from '../functions/useCoachingEdge';
import { usePracticeSession } from '../functions/usePracticeSession';
import { useScenePlayback } from '../functions/useScenePlayback';
import { useSilenceHint } from '../functions/useSilenceHint';
import { useTraineeReplay } from '../functions/useTraineeReplay';
import { useTraineeSend } from '../functions/useTraineeSend';
import { useVoiceHintHold } from '../functions/useVoiceHintHold';
import { scenePillText } from '../practiceCopy';
import { practiceEndpoint, practiceStart } from '../voice/practiceEndpoint';
import { PracticeComposer } from './components/controls/PracticeComposer';
import { StopSheet } from './components/controls/StopSheet';
import { VoiceNoticeLine } from './components/controls/VoiceNoticeLine';
import { CallTimer } from './components/customer/CallTimer';
import { PracticeThread } from './components/dialogue/PracticeThread';
import { CoachingEdge } from './components/edge/CoachingEdge';
import { CoachingStrip } from './components/edge/CoachingStrip';
import { HintLine } from './components/edge/HintLine';
import { EndBeat } from './components/ending/EndBeat';
import { ReplayVerdictSheet } from './components/ending/ReplayVerdictSheet';
import { DrillGoal } from './components/header/DrillGoal';
import { PracticeHeader } from './components/header/PracticeHeader';
import { SceneSubheader } from './components/header/SceneSubheader';

function stopLabelOf(scene: PracticeScene): string {
  if (scene.exam) return '交卷';
  return scene.channel === CoachingChannel.Phone ? '挂断' : '结束';
}

/** No start without the practice's socket: nothing is captured. */
const kNoStart: Record<string, string> = {};

/**
 * One practice (or moment replay) as a chat with the customer: who they
 * are and how they feel up top, the conversation, the coach's strip, and
 * the trainee's controls.
 */
export const PracticeSceneView = memo(
  ({
    scene,
    speak$,
    onToggleSpeak,
    onEnded,
    onReplayAgain,
    onBackToReport,
    onBack,
  }: {
    scene: PracticeScene;
    speak$: BehaviorSubject<boolean>;
    onToggleSpeak: () => void;
    /** After the closing beat: to the report, or back. */
    onEnded: (end: CoachingPracticeEnd) => void;
    /** A moment replay: redo it, or back to the report. */
    onReplayAgain: () => void;
    onBackToReport: () => void;
    /** Leaves the scene; an unfinished practice can be picked up later. */
    onBack: () => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const desktop = useIsDesktopLayout();
    const session = usePracticeSession(scene, speak$);
    const [lines] = useBehaviorSubject(session.lines$);
    const [streaming] = useBehaviorSubject(session.streaming$);
    const [phase] = useBehaviorSubject(session.phase$);
    const [mood] = useBehaviorSubject(session.mood$);
    const [linesLeft] = useBehaviorSubject(session.linesLeft$);
    const [failedLine] = useBehaviorSubject(session.failedLine$);
    const [speak] = useBehaviorSubject(speak$);
    const [stopping, setStopping] = useState(false);

    const [verdict, setVerdict] = useState<CoachingAnchor | null>(null);
    const playback = useScenePlayback(scene, session, speak$);
    const [playingTurn] = useBehaviorSubject(playback.playingTurn$);
    const [live] = useBehaviorSubject(playback.live$);
    useEffect(() => {
      const events = session.events.subscribe(event => {
        if (event.kind === 'verdict' && scene.target.kind === 'replay')
          setVerdict(event.verdict);
      });
      return () => events.unsubscribe();
    }, [scene, session]);

    const edge = useCoachingEdge(session, scene.areas);
    const hints = useSilenceHint(
      session,
      playback.speaking$,
      // Exams are bare; a replay shows its better line instead.
      scene.exam || scene.target.kind === 'replay'
        ? null
        : scene.target.attemptId,
      scene.fixedHint,
    );
    const [hint] = useBehaviorSubject(hints.hint$);
    const trainee = useTraineeSend(session, hints.takeAfterHint);
    const [traineeVoices] = useBehaviorSubject(trainee.traineeVoices$);
    const replay = useTraineeReplay<CoachingPracticeLine>(playback.hush);
    const [traineePlaying] = useBehaviorSubject(replay.playing$);
    // Talking quiets every voice: the customer's and the trainee's own.
    const quiet = useCallback(() => {
      playback.hush();
      replay.stop();
    }, [playback, replay]);
    // In the scene a voice plays from the device, as recorded.
    const playTrainee = useCallback(
      (line: CoachingPracticeLine, heard: TraineeVoice) =>
        replay.toggle(line, async () =>
          heard.audio ? { mime: 'audio/wav', audio: heard.audio } : null,
        ),
      [replay],
    );

    const capture = useRef<SpeechCaptureHandle>(null);
    const practiceVoice = useMemo(() => {
      try {
        return {
          endpoint: practiceEndpoint(config.aiEndpoint, __DEV__),
          start: practiceStart(scene.target.attemptId),
        };
      } catch (error) {
        // Without a socket there is no voice; typing still works.
        console.error('coaching speech capture unavailable', error);
        return null;
      }
    }, [scene.target.attemptId]);
    const voice = useVoiceTurn(
      capture,
      useCallback(() => practiceVoice?.start ?? kNoStart, [practiceVoice]),
      (line, spoken) => trainee.send(line, traineeVoiceOf(spoken)),
      quiet,
    );
    useVoiceHintHold(voice.state$, hints);
    const [voiceState] = useBehaviorSubject(voice.state$);
    const [voiceNotice] = useBehaviorSubject(voice.notice$);

    const used = scene.maxLines - linesLeft;
    const progress = [
      `第 ${Math.min(used + 1, scene.maxLines)} / 约 ${
        scene.expectedLines
      } 个来回`,
      ...(scene.difficulty != null ? [levelName(scene.difficulty)] : []),
      channelName(scene.channel),
    ].join(' · ');
    const scenePill = scenePillText(
      scene.channel,
      scene.target.kind === 'replay'
        ? null
        : scene.openingTask
        ? '你'
        : scene.customer.name,
    );
    const listening = phase.kind === 'listening' && !verdict;
    // Holding is open while the customer replies: the trainee may talk over.
    const canTalk = phase.kind !== 'ended' && !verdict;
    const [stopFailed, setStopFailed] = useState(false);

    const confirmStop = useCallback(async () => {
      setStopping(false);
      setStopFailed(!(await session.stop()));
    }, [session]);

    const awaitingReply = phase.kind === 'replying';
    return (
      <KeyboardAvoidingView
        style={{ flex: 1, backgroundColor: aiColors.background }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <PracticeHeader
          customer={scene.customer}
          progress={progress}
          mood={scene.exam ? null : mood}
          stopLabel={stopLabelOf(scene)}
          onBack={onBack}
          onStop={
            phase.kind === 'ended'
              ? null
              : // A replay leaves the report as it was: nothing to confirm.
              scene.target.kind === 'replay'
              ? onBackToReport
              : () => setStopping(true)
          }
        />
        <SceneSubheader
          title={scene.title}
          speak={speak}
          onToggleSpeak={onToggleSpeak}
          extra={
            scene.channel === CoachingChannel.Phone ? (
              <CallTimer running={phase.kind !== 'ended'} />
            ) : null
          }
        />
        {scene.goal ? <DrillGoal goal={scene.goal} /> : null}
        {scene.target.kind === 'replay' ? (
          <DrillGoal goal="重练这个时刻" plain />
        ) : null}
        <View style={{ flex: 1, flexDirection: 'row' }}>
          <View style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ flex: 1, width: '100%', maxWidth: rpx(640) }}>
              <PracticeThread
                lines={lines}
                streaming={streaming}
                replying={awaitingReply}
                scenePill={scenePill}
                openingTask={scene.openingTask}
                customerName={scene.customer.name}
                playingTurn={playingTurn}
                reveal$={playback.reveal$}
                voices$={playback.voices$}
                position$={playback.position$}
                // A tap would cut the reply being spoken.
                canPlay={turn =>
                  !awaitingReply && !live && playback.canPlay(turn)
                }
                onPlay={turn => {
                  replay.stop();
                  playback.play(turn);
                }}
                // While it is spoken on the web, the trainee's line shows where
                // it lands; a phone shows it in the hold overlay or the box.
                draft={
                  Platform.OS === 'web' &&
                  phase.kind !== 'ended' &&
                  voiceState.kind !== 'idle'
                    ? voiceState
                    : null
                }
                level$={voice.level$}
                traineeVoices={traineeVoices}
                traineePlaying={traineePlaying}
                onPlayTrainee={playTrainee}
              />
              <View style={{ gap: rpx(8), paddingBottom: rpx(8) }}>
                {stopFailed ? (
                  <View style={{ paddingHorizontal: rpx(16) }}>
                    <CardNotice tone="danger" text="没能结束，请再试一次" />
                  </View>
                ) : null}
                {failedLine ? (
                  <View style={{ paddingHorizontal: rpx(16) }}>
                    <CardNotice tone="danger" text="没发出去，再试一次" />
                    <SecondaryButton
                      title="重发"
                      onPress={() => trainee.resend(failedLine)}
                    />
                  </View>
                ) : null}
                {phase.kind !== 'ended' && linesLeft === 1 ? (
                  <Typography.Text
                    size={rpx(12)}
                    color={aiColors.mutedForeground}
                    style={{ textAlign: 'center' }}
                  >
                    还剩一轮
                  </Typography.Text>
                ) : null}
                {phase.kind !== 'ended' && hint ? (
                  <HintLine hint={hint} onDismiss={hints.dismiss} />
                ) : null}
                {phase.kind !== 'ended' &&
                voiceState.kind === 'idle' &&
                voiceNotice ? (
                  <VoiceNoticeLine notice={voiceNotice} />
                ) : null}
              </View>
              {/* The customer finishes speaking before the call ends on
                  screen: while their goodbye plays the controls stay,
                  disabled, then give way to the end. */}
              {phase.kind === 'ended' && !live ? (
                <View
                  style={{
                    borderTopWidth: 1,
                    borderTopColor: aiColors.border,
                    backgroundColor: aiColors.card,
                  }}
                >
                  <EndBeat
                    end={phase.end}
                    channel={scene.channel}
                    customerName={scene.customer.name}
                    exam={scene.exam}
                    drill={scene.format === CoachingCaseFormat.Drill}
                    onNext={() => onEnded(phase.end)}
                  />
                </View>
              ) : (
                <>
                  {scene.exam || desktop ? null : <CoachingStrip edge={edge} />}
                  <PracticeComposer
                    key={failedLine ?? ''}
                    canType={listening}
                    // Not while the last sentence is still being recognised.
                    canTalk={canTalk && voiceState.kind !== 'recognizing'}
                    initial={failedLine ?? ''}
                    onSend={(line, spoken) => {
                      voice.cancel();
                      trainee.send(line, spoken);
                    }}
                    onActivity={hints.activity}
                    onHint={
                      scene.exam || scene.target.kind === 'replay'
                        ? null
                        : () => void hints.ask()
                    }
                    hintShown={phase.kind !== 'ended' && !!hint}
                    voice={voice}
                  />
                </>
              )}
            </View>
          </View>
          {scene.exam || !desktop ? null : (
            <View
              style={{ borderLeftWidth: 1, borderLeftColor: aiColors.border }}
            >
              <CoachingEdge edge={edge} />
            </View>
          )}
        </View>
        {/* Kept until the customer's last words are heard: on iOS the
            capture's web view going away resets the audio session, which
            cut their goodbye off mid-sentence. */}
        {phase.kind !== 'ended' || live ? (
          practiceVoice ? (
            <SpeechCapture
              ref={capture}
              endpoint={practiceVoice.endpoint}
              onMessage={voice.receive}
            />
          ) : null
        ) : null}
        <ReplayVerdictSheet
          verdict={verdict}
          onAgain={onReplayAgain}
          onBack={onBackToReport}
        />
        <StopSheet
          visible={stopping}
          exam={scene.exam}
          onClose={() => setStopping(false)}
          onConfirm={confirmStop}
        />
      </KeyboardAvoidingView>
    );
  },
);
